import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  loginUser,
  loginUserWithGoogle,
  sendLoginOtp,
  verifyLoginOtp,
} from "../apis/auth";
import { clearStoredAuth, getStoredToken, isTokenExpired } from "../utils/auth";
import { toast } from "react-toastify";
import {
  LogIn,
  AlertCircle,
  Shield,
  Eye,
  EyeOff,
  Mail,
  Lock,
  Sparkles,
  KeyRound,
} from "lucide-react";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import OtpInput from "react-otp-input";

const persistLoginSession = (responseData) => {
  localStorage.setItem("token", responseData.token);
  localStorage.setItem("userAccessToken", responseData.token);
  if (responseData.user) {
    localStorage.setItem("user", JSON.stringify(responseData.user));
  }
  if (responseData.user?.role === "agent") {
    localStorage.setItem("userRole", "agent");
  } else {
    localStorage.removeItem("userRole");
  }
  localStorage.setItem("sections", JSON.stringify(responseData.sections || []));
  if (Array.isArray(responseData.permissions)) {
    localStorage.setItem("agentPermissions", JSON.stringify(responseData.permissions));
  } else {
    localStorage.removeItem("agentPermissions");
  }
};

const Login = () => {
  const [loginMethod, setLoginMethod] = useState("password");
  const [form, setForm] = useState({ email: "", password: "" });
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    document.title = "BluTec PING Login — Access Your WhatsApp Growth Dashboard";
    const description =
      "Securely sign in to BluTec PING to run WhatsApp broadcasts, automate chat, and track performance in real time.";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", description);

    const token = getStoredToken();
    if (token && isTokenExpired(token)) {
      clearStoredAuth();
    }
  }, []);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const resetOtpFlow = () => {
    setOtpSent(false);
    setOtp("");
    setError("");
  };

  const switchLoginMethod = (method) => {
    if (method === loginMethod) return;
    setLoginMethod(method);
    resetOtpFlow();
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    try {
      const response = await loginUser(form);
      if (response.data.success === false) {
        throw new Error(response.data.error || "Login failed.");
      }

      persistLoginSession(response.data);
      toast.success(response.data.message || "Login successful!");
      setTimeout(() => navigate("/dashboard"), 1000);
    } catch (err) {
      const message =
        err.response?.data?.error || err.message || "Login failed.";
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!form.email) {
      toast.error("Please enter your email");
      return;
    }

    setIsLoading(true);
    setError("");
    try {
      const response = await sendLoginOtp({ email: form.email });
      if (response.data.success === false) {
        throw new Error(response.data.error || "Failed to send OTP");
      }

      toast.success(response.data.message || "OTP sent to your email");
      setOtpSent(true);
      setOtp("");
    } catch (err) {
      const message =
        err.response?.data?.error || err.message || "Failed to send OTP";
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast.error("Please enter the 6-digit OTP");
      return;
    }

    setIsLoading(true);
    setError("");
    try {
      const response = await verifyLoginOtp({ email: form.email, otp });
      if (response.data.success === false) {
        throw new Error(response.data.error || "Invalid OTP");
      }

      persistLoginSession(response.data);
      toast.success(response.data.message || "Login successful!");
      setTimeout(() => navigate("/dashboard"), 1000);
    } catch (err) {
      const message =
        err.response?.data?.error || err.message || "Invalid OTP";
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setIsLoading(true);
    setError("");
    try {
      const response = await sendLoginOtp({ email: form.email });
      if (response.data.success) {
        toast.success("OTP resent to your email");
        setOtp("");
      } else {
        throw new Error(response.data.error || "Failed to resend OTP");
      }
    } catch (err) {
      const message = err.response?.data?.error || "Failed to resend OTP";
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async (credentialResponse) => {
    setIsGoogleLoading(true);
    try {
      const data = { token: credentialResponse.credential };
      const response = await loginUserWithGoogle(data);
      if (response.data.success) {
        persistLoginSession(response.data);
        toast.success(response.data.message || "Google sign-in successful!");
        setTimeout(() => navigate("/dashboard"), 1000);
      } else {
        toast.error(response.data.error || "Google Login failed.");
      }
    } catch (err) {
      toast.error("Google Login failed. Try again.");
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const googleClientId = process.env.REACT_APP_CLIENT_ID;
  const showOtpStep = loginMethod === "otp" && otpSent;

  return (
    <div className="h-screen w-screen overflow-hidden bg-white flex">
      {/* Left Side - Decorative */}
      <div
        className="hidden lg:flex lg:w-1/2 h-full relative overflow-hidden"
        style={{ backgroundColor: "#e9eeea" }}
      >
        <div className="absolute inset-0 flex flex-col items-center justify-center p-8">
          <div
            className="absolute top-20 left-20 w-64 h-64 rounded-full opacity-20"
            style={{ backgroundColor: "#17181b" }}
          />
          <div
            className="absolute bottom-20 right-20 w-96 h-96 rounded-full opacity-10"
            style={{ backgroundColor: "#17181b" }}
          />

          <div className="relative z-10 text-center max-w-lg">
            <div
              className="mb-6 inline-flex items-center gap-3 px-6 py-3 rounded-2xl shadow-lg"
              style={{ backgroundColor: "#EEFA94" }}
            >
              <div className="p-2 bg-[#17181b]/90 rounded-xl">
                <Sparkles className="text-white" size={24} />
              </div>
              <span className="text-3xl font-bold text-gray-900">BluTec PING</span>
            </div>

            <h1 className="text-4xl font-extrabold text-gray-900 mb-4 leading-tight">
              Transform Your WhatsApp Communication
            </h1>

            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              Powerful automation, intelligent chatbots, and seamless broadcasts — all in one platform
            </p>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white rounded-2xl p-4 border border-gray-200 shadow-sm">
                <div className="text-2xl font-bold text-gray-900 mb-1">10K+</div>
                <div className="text-xs text-gray-600 font-medium">Messages Sent</div>
              </div>
              <div className="bg-white rounded-2xl p-4 border border-gray-200 shadow-sm">
                <div className="text-2xl font-bold text-gray-900 mb-1">98%</div>
                <div className="text-xs text-gray-600 font-medium">Uptime</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 h-full flex items-center justify-center p-4 sm:p-8 overflow-y-auto">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-6">
            <div
              className="inline-flex items-center gap-3 px-6 py-3 rounded-2xl shadow-lg mb-4"
              style={{ backgroundColor: "#EEFA94" }}
            >
              <div className="p-2 bg-[#17181b]/90 rounded-xl">
                <Sparkles className="text-white" size={20} />
              </div>
              <span className="text-2xl font-bold text-gray-900">BluTec PING</span>
            </div>
          </div>

          <div className="mb-6 text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 mb-2">
              {showOtpStep ? "Verify your email" : "Welcome Back"}
            </h2>
            <p className="text-gray-600">
              {showOtpStep
                ? "Enter the 6-digit code we sent to your inbox"
                : "Sign in to access your dashboard"}
            </p>
          </div>

          <div className="flex justify-center mb-5">
            <span className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm">
              <div className="p-1 bg-[#17181b]/90 rounded-lg">
                <Shield size={14} className="text-white" />
              </div>
              {showOtpStep ? "Email OTP Verification" : "Secure Authentication"}
            </span>
          </div>

          {error && (
            <div className="mb-5 rounded-2xl border border-red-300 bg-red-50 p-4">
              <div className="flex items-start gap-3">
                <AlertCircle size={20} className="text-red-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {!showOtpStep && (
            <div className="mb-5 grid grid-cols-2 gap-2 rounded-xl bg-gray-100 p-1">
              <button
                type="button"
                onClick={() => switchLoginMethod("password")}
                className={`flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold transition-all ${
                  loginMethod === "password"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <Lock size={16} />
                Password
              </button>
              <button
                type="button"
                onClick={() => switchLoginMethod("otp")}
                className={`flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-semibold transition-all ${
                  loginMethod === "otp"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                <KeyRound size={16} />
                Email OTP
              </button>
            </div>
          )}

          {loginMethod === "password" && !showOtpStep && (
            <form onSubmit={handlePasswordLogin} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@company.com"
                    autoComplete="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    className="w-full rounded-xl border border-gray-200 bg-white pl-12 pr-4 py-3 text-base text-gray-900 placeholder-gray-400 shadow-sm outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-300 transition"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    value={form.password}
                    onChange={handleChange}
                    required
                    className="w-full rounded-xl border border-gray-200 bg-white pl-12 pr-12 py-3 text-base text-gray-900 placeholder-gray-400 shadow-sm outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-300 transition"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div className="flex justify-end">
                <a
                  href="/forget-password"
                  className="text-sm font-semibold text-gray-700 hover:text-gray-900 transition"
                >
                  Forgot password?
                </a>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full rounded-xl py-3.5 text-base font-semibold text-white bg-[#17181b]/90 shadow-md hover:shadow-lg hover:bg-[#17181b]/80 transition-all focus:outline-none focus:ring-2 focus:ring-[#17181b]/30 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-5 h-5 border-2 border-white/60 border-t-transparent rounded-full animate-spin" />
                    <span>Signing in...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <LogIn size={18} />
                    <span>Sign In</span>
                  </div>
                )}
              </button>
            </form>
          )}

          {loginMethod === "otp" && !showOtpStep && (
            <form onSubmit={handleSendOtp} className="space-y-4">
              <div>
                <label htmlFor="otp-email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    id="otp-email"
                    name="email"
                    type="email"
                    placeholder="you@company.com"
                    autoComplete="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    className="w-full rounded-xl border border-gray-200 bg-white pl-12 pr-4 py-3 text-base text-gray-900 placeholder-gray-400 shadow-sm outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-300 transition"
                  />
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  We&apos;ll send a 6-digit code to your registered email
                </p>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full rounded-xl py-3.5 text-base font-semibold text-white bg-[#17181b]/90 shadow-md hover:shadow-lg hover:bg-[#17181b]/80 transition-all focus:outline-none focus:ring-2 focus:ring-[#17181b]/30 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-5 h-5 border-2 border-white/60 border-t-transparent rounded-full animate-spin" />
                    <span>Sending OTP...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <Mail size={18} />
                    <span>Send OTP</span>
                  </div>
                )}
              </button>
            </form>
          )}

          {showOtpStep && (
            <form onSubmit={handleVerifyOtp} className="space-y-5">
              <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="mb-5 rounded-xl bg-gray-50 px-4 py-3 text-center">
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    Code sent to
                  </p>
                  <p className="mt-1 text-sm font-semibold text-gray-900 break-all">
                    {form.email}
                  </p>
                </div>

                <div className="mb-4">
                  <OtpInput
                    value={otp}
                    onChange={setOtp}
                    numInputs={6}
                    containerStyle={{
                      display: "flex",
                      justifyContent: "center",
                      gap: "10px",
                      width: "100%",
                    }}
                    renderInput={(props) => (
                      <input
                        {...props}
                        className="!w-11 !h-14 sm:!w-12 sm:!h-14 rounded-xl border-2 border-gray-200 bg-white text-center text-xl font-bold text-gray-900 shadow-sm outline-none transition focus:border-[#17181b] focus:ring-2 focus:ring-[#17181b]/20 disabled:opacity-50"
                      />
                    )}
                    inputType="tel"
                    shouldAutoFocus
                  />
                </div>

                <p className="text-center text-xs text-gray-500">
                  Enter the 6-digit code from your email. It expires in 5 minutes.
                </p>
              </div>

              <button
                type="submit"
                disabled={isLoading || otp.length !== 6}
                className="w-full rounded-xl py-3.5 text-base font-semibold text-white bg-[#17181b]/90 shadow-md hover:shadow-lg hover:bg-[#17181b]/80 transition-all focus:outline-none focus:ring-2 focus:ring-[#17181b]/30 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white/60 border-t-transparent rounded-full animate-spin" />
                    <span>Verifying...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <LogIn size={18} />
                    <span>Verify &amp; Sign In</span>
                  </div>
                )}
              </button>

              <div className="flex items-center justify-between gap-4 px-1">
                <button
                  type="button"
                  onClick={resetOtpFlow}
                  disabled={isLoading}
                  className="text-sm font-semibold text-gray-600 hover:text-gray-900 transition-colors disabled:opacity-50"
                >
                  ← Change email
                </button>
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={isLoading}
                  className="text-sm font-semibold text-gray-600 hover:text-gray-900 transition-colors disabled:opacity-50"
                >
                  Resend OTP
                </button>
              </div>
            </form>
          )}

          {!showOtpStep && (
            <>
              <div className="flex items-center my-6">
                <div className="flex-1 border-t border-gray-200" />
                <span className="px-4 text-sm text-gray-500 font-medium">OR</span>
                <div className="flex-1 border-t border-gray-200" />
              </div>

              <div className="flex justify-center mb-6">
                <GoogleOAuthProvider clientId={googleClientId}>
                  <GoogleLogin
                    onSuccess={handleGoogleLogin}
                    onError={() => toast.error("Google Login Failed")}
                    theme="outline"
                    text="continue_with"
                    width="100%"
                    shape="rectangular"
                    size="large"
                    logo_alignment="left"
                    disabled={isGoogleLoading}
                  />
                </GoogleOAuthProvider>
              </div>
            </>
          )}

          <div className="text-center">
            <p className="text-sm text-gray-600">
              Don&apos;t have an account?{" "}
              <a
                href="/signup"
                className="font-semibold text-gray-900 hover:text-gray-700 transition"
              >
                Sign up
              </a>
            </p>
          </div>

          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500">
              Need help?{" "}
              <a
                href="mailto:sales@blutec.ai"
                className="text-gray-700 hover:text-gray-900 font-semibold transition"
              >
                Contact Support
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
